/**
 * Module dependencies.
 */

import { Checkbox, Popconfirm } from 'antd';
import { useCrudForm } from 'app/components/organisms/crud/crud-form-provider';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { compact } from 'lodash';
import { useGetCCICodes } from 'app/hooks/app-requests/ccis/use-get-cci-codes';

/**
 * `Props` type.
 */

type Props = {
  deleteRelation: (cciAId: number, cciBId: number) => void;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  margin-top: 40px;
  border: 1px solid #ccc;
  padding: 16px 16px 0 16px;
`;

/**
 * `Header` styled component.
 */

const Header = styled.div`
  background-color: #eeeeee;
  display: flex;
  flex-direction: row;
  margin: -16px -16px 0 -16px;
  padding: 0 16px 0 16px;
`;

/**
 * `Col` styled component.
 */

const Col = styled.div<{ noFill?: boolean }>`
  flex: 5;
  text-align: center;
  padding: 16px;

  ${ifProp(
    'noFill',
    css`
      flex: 1;
      display: flex;
      justify-content: center;
    `
  )}
`;

/**
 * `Line` styled component.
 */

const Line = styled.div<{ isLast?: boolean }>`
  display: flex;
  border-bottom: 1px solid #ccc;

  ${ifProp(
    'isLast',
    css`
      border-bottom: none;
    `
  )}
`;

/**
 * `Close` styled component.
 */

const Close = styled.div`
  border: 1px solid red;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: red;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    color: white;
    background-color: red;
  }
`;

/**
 * Export `ShowRelations` component.
 */

export function ShowRelations({ deleteRelation }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { data } = useGetCCICodes();
  const form = useCrudForm();
  const { getValues } = form;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const values = getValues('relations') ?? [];
  const resolvedValues = useMemo(() => {
    return compact(
      values.map((relation: any) => {
        const cciA = data?.find((cci: any) => cci.cci === relation.cciA);
        const cciB = data?.find((cci: any) => cci.cci === relation.cciB);

        if (!cciA || !cciB) {
          return null;
        }

        return { severity: relation.severity, cciA, cciB };
      })
    );
  }, [data, values]);

  const changeSeverity = useCallback(
    (checked: boolean, cciA: string, cciB: string) => {
      const relations = getValues('relations') ?? [];
      const updatedRelations = relations.map((relation: any) => {
        if (relation.cciA === cciA && relation.cciB === cciB) {
          return { ...relation, severity: checked };
        }
        return relation;
      });

      form.setValue('relations', updatedRelations);
    },
    [form, getValues]
  );

  return (
    <Wrapper>
      <Header>
        <Col noFill>{translate('cciAssociation.labels.severity')}</Col>

        <Col>{translate('cciAssociation.labels.cciA')}</Col>

        <Col>{translate('cciAssociation.labels.cciB')}</Col>

        <Col noFill>{translate('common.labels.options')}</Col>
      </Header>

      {resolvedValues.map((relation: any, index: number) => (
        <Line isLast={index === resolvedValues.length - 1} key={index}>
          <Col noFill>
            <Checkbox
              checked={relation.severity}
              onChange={value => {
                changeSeverity(value.target.checked, relation.cciA.cci, relation.cciB.cci);
              }}
            />
          </Col>

          <Col>{relation.cciA?.cci ?? 'N/A'}</Col>

          <Col>{relation.cciB?.cci ?? 'N/A'}</Col>

          <Col noFill>
            <Popconfirm
              okButtonProps={{ loading: false }}
              style={{
                backgroundColor: 'blue'
              }}
              {...{ onClick: (event: any) => event.stopPropagation() }}
              onConfirm={(event: any) => {
                event.stopPropagation();
                deleteRelation(relation.cciA.cci, relation.cciB.cci);
              }}
              title={translate('removeAction')}
            >
              <Close>{'X'}</Close>
            </Popconfirm>
          </Col>
        </Line>
      ))}
    </Wrapper>
  );
}
